import { useEffect, useState } from 'react';
import { Dropdown } from '../ui';
import { NOTE_REQUIREMENTS, fetchNoteTemplates, type NoteTemplate } from '../../lib/noteTemplates';
import AttachmentDropzone from './AttachmentDropzone';
import type { AttachmentPicker } from './attachmentPicker';
import ModelPicker from './ModelPicker';

interface SpecQueueNoteFieldsProps {
  note: string;
  onNoteChange: (note: string) => void;
  /** The canned note picked in the dropdown; null = "No note". Its requirements are the parent's to check on Confirm. */
  template: NoteTemplate | null;
  onTemplateChange: (template: NoteTemplate | null) => void;
  picker: AttachmentPicker;
  disabled: boolean;
  /** Pinned model for the new job. Omit both to hide the picker. */
  model?: string | null;
  onModelChange?: (model: string | null) => void;
  style?: React.CSSProperties;
}

/**
 * "Note for Owlbert" + attachments + model — the human context that travels
 * with a queue item. Shared by both spec queue panels so they read identically.
 * A canned note (Agents → Note templates) picked from the dropdown replaces the
 * note's text and stays editable; the dropdown hides when there are none.
 * The pick, not the wording, carries the template's requirements: editing the
 * text keeps them, "No note" drops them.
 */
export default function SpecQueueNoteFields({
  note,
  onNoteChange,
  template,
  onTemplateChange,
  picker,
  disabled,
  model,
  onModelChange,
  style,
}: SpecQueueNoteFieldsProps) {
  const [templates, setTemplates] = useState<NoteTemplate[]>([]);

  // No templates is better than no drawer: a failed load just hides the dropdown.
  useEffect(() => {
    let live = true;
    fetchNoteTemplates()
      .then((t) => live && setTemplates(t))
      .catch((err) => console.error('Error loading note templates:', err));
    return () => {
      live = false;
    };
  }, []);

  const pickTemplate = (id: string) => {
    const t = templates.find((x) => x.id === id) ?? null;
    // "No note" clears a note that is still the template's text; anything typed over it stays, now freehand.
    if (!t && template && note === template.body) onNoteChange('');
    if (t) onNoteChange(t.body);
    onTemplateChange(t);
  };
  const needs = template ? NOTE_REQUIREMENTS.filter((r) => template.requirements[r.key]) : [];

  return (
    <div style={style}>
      {onModelChange ? (
        <ModelPicker
          variant="field"
          value={model ?? null}
          onChange={onModelChange}
          disabled={disabled}
          hint="Which LLM runs this job. Default runs on the agent's own model, currently Opus 5.5. Changeable from the queue card until an agent picks it up."
          style={{ marginBottom: '14px' }}
        />
      ) : null}
      {templates.length ? (
        <div style={{ marginBottom: '14px', ...(disabled ? { pointerEvents: 'none', opacity: 0.6 } : null) }}>
          <Dropdown
            label="Canned note"
            placeholder="Load a note template…"
            options={[{ value: '', label: 'No note' }, ...templates.map((t) => ({ value: t.id, label: t.name }))]}
            value={template?.id ?? ''}
            onChange={(e) => pickTemplate(e.target.value)}
          />
          {needs.length ? (
            <p style={{ margin: '6px 0 0', fontSize: 'var(--text-xs)', color: 'var(--text-muted)', lineHeight: 1.5 }}>
              This note needs: {needs.map((r) => r.label.replace(/^Requires /, '')).join(', ')}.
            </p>
          ) : null}
        </div>
      ) : null}
      <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        <span className="t-eyebrow">Note for Owlbert</span>
        <textarea
          value={note}
          onChange={(e) => onNoteChange(e.target.value)}
          placeholder="Optional. Travels with this queue item."
          rows={4}
          disabled={disabled}
          style={{
            width: '100%',
            resize: 'vertical',
            minHeight: '88px',
            padding: '10px 12px',
            borderRadius: 'var(--radius-sm)',
            border: '1px solid var(--border-hairline)',
            background: 'var(--surface-input)',
            color: 'var(--text-body)',
            fontFamily: 'var(--font-ui)',
            fontSize: 'var(--text-sm)',
            lineHeight: 1.45,
            outline: 'none',
          }}
        />
      </label>
      <div style={{ marginTop: '14px' }}>
        <div className="t-eyebrow" style={{ marginBottom: '6px' }}>
          Attachments{picker.files.length > 0 ? ` (${picker.files.length})` : ''}
        </div>
        <AttachmentDropzone
          compact
          files={picker.files}
          error={picker.error}
          onAdd={picker.add}
          onRemove={picker.remove}
          disabled={disabled}
        />
        <p style={{ margin: '6px 0 0', fontSize: 'var(--text-xs)', color: 'var(--text-faint)', lineHeight: 1.5 }}>
          Specs, .md notes, screenshots, data — anything Owlbert should read first. Links land in the note.
        </p>
      </div>
    </div>
  );
}
